/**
 * Utility for making API requests with proper error handling and authentication
 */

// Default request options with credentials
const defaultOptions = {
  credentials: 'include',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
};

/**
 * Make an API request with proper error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Response data
 */
export const apiRequest = async (url, options = {}) => {
  try {
    // Merge default options with provided options
    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers
      }
    };
    
    const response = await fetch(url, mergedOptions);
    
    // For 204 No Content, return empty object
    if (response.status === 204) {
      return {};
    }
    
    // For other responses, try to parse JSON
    const data = await response.json().catch(() => null);
    
    if (!response.ok) {
      // If response contains error data, throw it
      if (data && data.error) {
        throw new Error(data.error);
      }
      throw new Error(response.statusText || `Server responded with status ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error(`API request failed for ${url}:`, error);
    throw error;
  }
};

/**
 * GET request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Response data
 */
export const get = (url, options = {}) => {
  return apiRequest(url, { ...options, method: 'GET' });
};

/**
 * POST request
 * @param {string} url - The URL to fetch
 * @param {Object} body - Request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Response data
 */
export const post = (url, body, options = {}) => {
  return apiRequest(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body)
  });
};

/**
 * PUT request
 * @param {string} url - The URL to fetch
 * @param {Object} body - Request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Response data
 */
export const put = (url, body, options = {}) => {
  return apiRequest(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body)
  });
};

/**
 * PATCH request
 * @param {string} url - The URL to fetch
 * @param {Object} body - Request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Response data
 */
export const patch = (url, body = {}, options = {}) => {
  return apiRequest(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

/**
 * DELETE request
 * @param {string} url - The URL to fetch
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Response data
 */
export const del = (url, options = {}) => {
  return apiRequest(url, { ...options, method: 'DELETE' });
};

export default {
  get,
  post,
  put,
  patch,
  delete: del,
  request: apiRequest
};
